'use client';

import { useWorkbenchConfig } from '@/components/Contexts/WorkbenchConfigContext';
import { useApiClient } from '@/hooks/useApiClient';

import type { DevicePreset, Position } from '@/types/feedback';

interface UpsertCommentLocation {
  position: Position;
  fallbackXPct: number;
  fallbackYPct: number;
  breakpoint: { id: string; minWidth: number };
}

interface UpsertCommentParams {
  message?: string;
  commentId?: string;
  revision?: string | null;
  devicePreset?: DevicePreset;
  // Optional: an edit of an existing comment may update message/status only and
  // leave the stored location untouched (the update RPC is field-masked). Adding
  // a new comment must always supply a location.
  location?: UpsertCommentLocation;
}

interface UpsertCommentResult {
  commentId: string;
  revision: string | null;
}

export function useUpsertComment() {
  const apiClient = useApiClient();
  // feedbackId, metaSiteId and commenterId travel inside the sealed context —
  // the route reads them from there and ignores anything else in the body.
  const { sealedContext } = useWorkbenchConfig();

  async function upsertComment(
    params: UpsertCommentParams,
  ): Promise<UpsertCommentResult> {
    return apiClient
      .post<UpsertCommentResult>('api/upsert-comment', {
        ctx: sealedContext,
        ...params,
      })
      .then((r) => r.data);
  }

  return { upsertComment };
}
